import { FC, ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  ExternalLink,
  Footer,
  Image,
  Notification,
  NotificationProps,
} from '@app/components';
import { useBranding } from '@app/hooks';
import { Route, ShapeWithClassName, supportSiteUrl } from '@app/constants';

import { AuthPageWrapperContext } from './AuthPageWrapperContext';
import { Form } from './Form';
import { FormTitle } from './FormTitle';
import { Row } from './Row';
import { SubmitButton } from './SubmitButton';
import { RowMessage } from './RowMessage';
import { InputGroup } from './InputGroup';

import './AuthPageWrapper.scss';

interface AuthPageWrapperProps {
  title?: string;
  notification?: NotificationProps;
  titlePrompt?: ReactElement;
}

interface AuthPageWrapperComposite
  extends FC<ShapeWithClassName<AuthPageWrapperProps>> {
  Row: typeof Row;
  Form: typeof Form;
  SubmitButton: typeof SubmitButton;
  RowMessage: typeof RowMessage;
  FormTitle: typeof FormTitle;
  InputGroup: typeof InputGroup;
}

export const AuthPageWrapper: AuthPageWrapperComposite = ({
  title,
  notification,
  titlePrompt,
  className,
  children,
}) => {
  const { logo } = useBranding();
  const { $t } = useIntl();

  const cnBem = createBemBlockBuilder(['authPageWrapper', className]);

  return (
    <AuthPageWrapperContext.Provider
      value={{
        cnBem,
      }}
    >
      <div className={cnBem('__wrapper')}>
        <header className={cnBem('__header')}>
          <div className={cn('container', cnBem('__headerInner'))}>
            <Link to={Route.search}>
              <Image
                className="logo"
                src={logo.url}
                alt={logo.altText}
                fetchpriority="high"
              />
            </Link>
            <ExternalLink href={supportSiteUrl}>
              {$t({
                id: 'login.supportLink',
                defaultMessage: 'EBSCO Support Site',
              })}
            </ExternalLink>
          </div>
        </header>
        <main className={cnBem('__mainWrapper')} data-auto="auth-wrapper">
          <div className={cnBem('__container')}>
            {(title ?? titlePrompt) && (
              <div className={cnBem('__heading')} data-auto="auth-heading">
                {title && (
                  <h1 className={cnBem('__title')} data-auto="auth-title">
                    {title}
                  </h1>
                )}
                {titlePrompt && (
                  <div
                    className={cnBem('__titlePrompt')}
                    data-auto="auth-subtitle"
                  >
                    {titlePrompt}
                  </div>
                )}
              </div>
            )}
            {notification && (
              <div className={cnBem('__section')} data-auto="auth-notification">
                <Notification
                  className={cnBem('__notification')}
                  {...notification}
                />
              </div>
            )}
            {children}
          </div>
        </main>
        <Footer />
      </div>
    </AuthPageWrapperContext.Provider>
  );
};

AuthPageWrapper.Row = Row;
AuthPageWrapper.Form = Form;
AuthPageWrapper.SubmitButton = SubmitButton;
AuthPageWrapper.RowMessage = RowMessage;
AuthPageWrapper.FormTitle = FormTitle;
AuthPageWrapper.InputGroup = InputGroup;
